/**
 * @fileoverview BlogPost.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import { blogArticles } from '../utils/blogData'
import { apiFetch } from '../utils/api'
import SEO from '../components/ui/SEO'
import TourFeatureCard from '../components/tours/TourFeatureCard'

function BlogPost() {
  const { slug } = useParams();
  const [tours, setTours] = useState([]);

  const article = blogArticles.find(art => art.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    apiFetch('/tours?status=active')
      .then(res => {
        if (res && res.data) {
          const formatted = res.data.map(t => ({
            id: t._id,
            name: t.title,
            slug: t.slug,
            category: t.categories?.[0] || 'Tour',
            duration: `${t.duration} Days | ${t.duration - 1} Nights`,
            durationDays: t.duration,
            price: t.price,
            description: t.description || '',
            features: t.includes && t.includes.length > 0 ? t.includes.slice(0, 4) : ['hotel', 'meal', 'car', 'guide'],
            image: (t.image && typeof t.image === 'object' && (t.image.l || t.image.h)) 
              ? { l: t.image.l || t.image.h, h: t.image.h || t.image.l }
              : { l: (t.images && t.images[0]) || t.image || 'https://images.unsplash.com/photo-1546708973-b339540b5162?w=800', h: (t.images && t.images[0]) || t.image || 'https://images.unsplash.com/photo-1546708973-b339540b5162?w=800' }
          }));
          setTours(formatted);
        }
      });
  }, [slug]);

  if (!article) {
    return (
      <div className='flex flex-col w-full min-h-screen justify-center items-center pt-24 md:pt-32 px-6 text-center bg-gray-50'>
        <h2 className='text-3xl font-black text-gray-800 uppercase mb-4'>Article Not Found</h2>
        <p className='text-gray-500 mb-8 max-w-md'>The blog article you are looking for does not exist or has been removed.</p>
        <Link to="/blog" className='px-8 py-4 bg-KITH-primary text-white font-bold rounded-2xl hover:bg-KITH-primary-dark transition-all shadow-md'>
          Back to Blog List
        </Link>
      </div>
    );
  }

  // Schema for Article
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "image": [article.image],
    "datePublished": article.publishedDate,
    "author": {
      "@type": "Person",
      "name": article.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "KITH Travels Sri Lanka",
      "logo": {
        "@type": "ImageObject",
        "url": "https://kithtravels.com/logo.jpg"
      }
    },
    "description": article.summary
  };

  // Find related tours
  const relatedToursData = tours.filter(tour => article.relatedTours.includes(tour.slug));

  return (
    <div className='flex flex-col w-full min-h-screen pb-20 text-left pt-24 md:pt-32 bg-gray-50'>
      <SEO 
        title={article.title}
        description={article.summary}
        keywords={`${article.title}, Sri Lanka travel blog, Sri Lanka guides`}
        image={article.image}
        schema={articleSchema}
      />

      {/* Article Hero Banner */}
      <div className='px-[5%] md:px-[10%]'>
        <div className='relative w-full h-[40vh] md:h-[50vh] rounded-[2.5rem] overflow-hidden shadow-lg'>
          <img 
            src={article.image} 
            alt={article.title} 
            className='absolute inset-0 w-full h-full object-cover'
          />
          <div className='absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent'></div>
          <div className='absolute bottom-8 left-8 md:left-12 max-w-3xl flex flex-col gap-3 text-left z-10'>
            <span className='px-3 py-1 bg-KITH-primary text-white text-[10px] font-bold rounded-full uppercase tracking-widest w-fit shadow-md'>
              {article.readingTime}
            </span>
            <h1 className='text-2xl md:text-4xl lg:text-5xl font-black text-white leading-tight'>{article.title}</h1>
            <div className='flex items-center gap-4 text-white/70 text-xs md:text-sm mt-2'>
              <span>By <strong>{article.author}</strong></span>
              <span>•</span>
              <span>{new Date(article.publishedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content & Recommendations */}
      <div className='px-[5%] md:px-[10%] mt-12 grid grid-cols-1 lg:grid-cols-12 gap-16'>
        {/* Left Column: Post Body */}
        <div className='lg:col-span-8 bg-white p-8 md:p-12 rounded-[2.5rem] border border-gray-100 shadow-sm'>
          <div className='prose prose-lg max-w-none text-gray-700 leading-relaxed text-justify text-sm md:text-base'>
            <ReactMarkdown>{article.content}</ReactMarkdown>
          </div>
          
          <div className='border-t border-gray-100 mt-12 pt-8 flex items-center justify-between'>
            <Link to="/blog" className='text-KITH-primary font-bold text-sm flex items-center gap-2 hover:underline'>
              <i className='fi fi-br-arrow-left text-[10px] mt-[1px]'></i> Back to Blog
            </Link>
            <div className='flex gap-3 text-gray-400'>
              <span className='text-xs font-semibold uppercase tracking-wider'>Share:</span>
              <a href={`https://facebook.com/sharer/sharer.php?u=${window.location.href}`} target="_blank" rel="noreferrer" className='hover:text-KITH-primary' aria-label="Share on Facebook"><i className='fi fi-brands-facebook'></i></a>
              <a href={`https://twitter.com/intent/tweet?url=${window.location.href}`} target="_blank" rel="noreferrer" className='hover:text-KITH-primary' aria-label="Share on Twitter"><i className='fi fi-brands-twitter'></i></a>
            </div>
          </div>
        </div>

        {/* Right Column: Related Tour Recommendations */}
        <div className='lg:col-span-4 flex flex-col gap-8'>
          <h3 className='text-xl md:text-2xl font-black text-gray-800 leading-none'>Recommended Tours</h3>
          {relatedToursData.length > 0 ? (
            <div className='flex flex-col gap-8'>
              {relatedToursData.map((tour, index) => (
                <TourFeatureCard key={tour.id} tour={tour} index={index} />
              ))}
            </div>
          ) : (
            <div className='bg-white p-8 rounded-3xl border border-gray-100 text-center text-gray-400 font-medium text-sm'>
              No related tours recommended.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BlogPost
